// ── The one Esc stack, for both window shells (FS-2, batch 12 tail T2; TL-10) ──────────────────────────────────
// CompanyWorkspace/dialog.tsx's `Dialog` and MerchantDoc/shell.tsx's `DocWindow` each kept a stack of their own, so an
// Esc pressed with BOTH open reached both: the staff invoice window (a DocWindow) with its "Reverse JV-…" entries panel
// (a Dialog) over it closed the panel AND the invoice form behind it — or asked to discard the form the operator could
// not even see. Both shells now register here, on one stack, and only the winner acts.
//
// Esc belongs to the INNER-most window only. Both a window and the window it opened listen on the document, so without
// this a single Esc would close both and the operator would lose the form behind as well.
//
// ⛔ "Last mounted wins" was WRONG, and measurably so: with a nested Dialog the OUTER window answered Esc and the inner
// one never did — the exact harm this stack exists to prevent, only quieter, because the operator dismissing a small
// sub-dialog was asked about discarding the big form behind it. Two reasons, both structural rather than accidental:
// React flushes a child's mount effect BEFORE its parent's, so a window nested inside a window pushes first and the
// parent lands on top; and the registration effect re-ran whenever `close` changed identity (a flipped `dirty`, or the
// inline `onClose={() => …}` the panes pass being rebuilt on a re-render), which re-pushed the outer window above an
// inner one that was already open.
// So the winner is chosen by NESTING DEPTH — carried down by context (EscDepthCtx), therefore unaffected by effect order
// — with the later registration winning a tie between siblings. And registration is mount-only: the handler reads the
// current `close` through a ref, so it still cannot hold a stale `dirty` without the stack order depending on re-renders.
//
// The depth is ONE number for both shells: a Dialog opened inside a DocWindow is one level deeper than it (the in-books
// "Reverse JV-…" panel answers Esc alone), and a DocWindow opened inside a Dialog likewise. The portal's own windows
// (MerchantDoc on PORTAL_LAYER) keep what they had: two of them are never nested, so the later one wins the tie — the
// top-most, as before.
import React from 'react';

const escStack: { depth: number; tok: object }[] = [];

/** The window Esc belongs to now: the deepest; on a depth tie, the later registration. */
export const escWinner = (): object | undefined => {
  let best: { depth: number; tok: object } | undefined;
  for (const e of escStack) if (!best || e.depth >= best.depth) best = e;
  return best?.tok;
};

/** The nesting depth of the window a component is rendered inside (0 = none). A window reads it, registers at
 *  depth + 1, and provides that depth to its children. */
export const EscDepthCtx = React.createContext(0);

/** Register a window on the one Esc stack at `depth`, for as long as it is mounted. `close` is the window's gated close
 *  (it asks before discarding); it is read through a ref, so a re-render never re-registers the window. */
export function useEscLayer(close: () => void, depth: number): void {
  const closeRef = React.useRef(close);
  React.useEffect(() => { closeRef.current = close; }, [close]);
  const token = React.useRef({});
  React.useEffect(() => {
    const mine = token.current;
    escStack.push({ depth, tok: mine });
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (escWinner() !== mine) return;                                 // not the inner-most window
      e.stopPropagation();
      closeRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      const i = escStack.findIndex(x => x.tok === mine);
      if (i >= 0) escStack.splice(i, 1);
    };
  }, [depth]);
}
